import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { ArrowLeft, Trophy, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const QuizBattle = () => {
  const navigate = useNavigate();
  const { fetchProfile } = useAuth();
  const [timeLeft, setTimeLeft] = useState(120);
  const [score, setScore] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);

  const questions = [
    {
      question: 'What is the primary purpose of blockchain technology?',
      options: ['To mine cryptocurrency', 'To create a distributed, immutable ledger', 'To replace the internet', 'To store files'],
      correct: 1
    },
    {
      question: 'Which consensus mechanism does Bitcoin use?',
      options: ['Proof of Stake', 'Proof of Work', 'Proof of Authority', 'Delegated Proof of Stake'],
      correct: 1
    },
    {
      question: 'What does DeFi stand for?',
      options: ['Digital Finance', 'Decentralized Finance', 'Defined Finance', 'Distributed Finance'],
      correct: 1
    },
    {
      question: 'What is a smart contract?',
      options: ['A legal document', 'Self-executing code on blockchain', 'A mining algorithm', 'A type of cryptocurrency'],
      correct: 1
    },
    {
      question: 'What does NFT stand for?',
      options: ['New Financial Token', 'Non-Fungible Token', 'Network File Transfer', 'Next Future Technology'],
      correct: 1
    },
    {
      question: 'Which blockchain platform introduced smart contracts?',
      options: ['Bitcoin', 'Ethereum', 'Litecoin', 'Ripple'],
      correct: 1
    },
    {
      question: 'What is gas in Ethereum?',
      options: ['A type of cryptocurrency', 'The fuel for miners', 'The cost of computational work', 'A consensus mechanism'],
      correct: 2
    },
    {
      question: 'What is a 51% attack?',
      options: ['A virus', 'When majority of network power is controlled', 'A mining technique', 'A type of wallet'],
      correct: 1
    },
    {
      question: 'What is the maximum supply of Bitcoin?',
      options: ['10 million', '21 million', '50 million', 'Unlimited'],
      correct: 1
    },
    {
      question: 'What does DAO stand for?',
      options: ['Digital Asset Organization', 'Decentralized Autonomous Organization', 'Distributed Access Operation', 'Data Access Object'],
      correct: 1
    }
  ];

  useEffect(() => {
    if (gameStarted && timeLeft > 0 && !gameOver) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      endGame();
    }
  }, [timeLeft, gameStarted, gameOver]);

  const startGame = () => {
    setGameStarted(true);
  };

  const handleAnswer = (index) => {
    if (showResult) return;
    
    setSelectedAnswer(index);
    setShowResult(true);
    
    if (index === questions[currentQuestion].correct) {
      setScore(score + 200);
      toast.success('Correct! +200 points');
    } else {
      toast.error('Incorrect answer');
    }

    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer(null);
        setShowResult(false);
      } else {
        endGame();
      }
    }, 2000);
  };

  const endGame = async () => {
    if (gameOver) return;
    setGameOver(true);
    
    try {
      const response = await axios.post(`${API}/games/submit`, {
        game_type: 'quiz-battle',
        score: score,
        time_taken: 120 - timeLeft
      });
      toast.success(`Quiz complete! Earned ${response.data.xp_gained} XP`);
      await fetchProfile();
    } catch (error) {
      console.error('Failed to submit score', error);
    }
  };

  if (!gameStarted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-start-screen">
            <Trophy className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Quiz Battle</h1>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Answer 10 blockchain questions as fast as you can! You have 120 seconds. Each correct answer earns 200 points.
            </p>
            <Button
              onClick={startGame}
              data-testid="start-game-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 hover:shadow-green-glow uppercase tracking-wide px-8 py-6 text-base h-auto"
            >
              Start Battle
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              data-testid="back-button"
              className="mt-4 text-zinc-400 hover:text-primary"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-over-screen">
            <Trophy className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Battle Complete!</h1>
            <div className="mb-6">
              <div className="text-5xl font-mono font-bold text-primary mb-2" data-testid="final-score">{score}</div>
              <div className="text-zinc-400">Final Score</div>
              <div className="mt-4 text-zinc-400">
                Correct: <span className="text-primary font-mono">{score / 200}/{questions.length}</span>
              </div>
            </div>
            <Button
              onClick={() => window.location.reload()}
              data-testid="play-again-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 uppercase tracking-wide px-8 py-3 mb-3 w-full"
            >
              Battle Again
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              data-testid="back-to-games-button"
              className="text-zinc-400 hover:text-primary w-full"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/games')}
          data-testid="back-to-games-button"
          className="mb-4 text-zinc-400 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Exit Battle
        </Button>

        {/* Game Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-grotesk font-bold text-white">Quiz Battle</h1>
            <p className="text-zinc-400 text-sm">Question {currentQuestion + 1} of {questions.length}</p>
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-center" data-testid="game-timer">
              <Clock className="w-5 h-5 text-primary mx-auto mb-1" />
              <div className="text-2xl font-mono font-bold text-primary">{timeLeft}s</div>
            </div>
            <div className="text-center" data-testid="game-score">
              <Trophy className="w-5 h-5 text-primary mx-auto mb-1" />
              <div className="text-2xl font-mono font-bold text-white">{score}</div>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-8 mb-6">
          <h2 className="text-2xl font-grotesk font-semibold text-white mb-8" data-testid="question-text">
            {questions[currentQuestion].question}
          </h2>

          <div className="space-y-4">
            {questions[currentQuestion].options.map((option, index) => {
              const isSelected = selectedAnswer === index;
              const isCorrect = index === questions[currentQuestion].correct;
              const showColors = showResult;

              return (
                <button
                  key={index}
                  onClick={() => handleAnswer(index)}
                  disabled={showResult}
                  data-testid={`option-${index}`}
                  className={`w-full p-4 rounded-lg text-left font-medium transition-all border-2 ${
                    showColors
                      ? isCorrect
                        ? 'bg-primary/20 border-primary text-primary'
                        : isSelected
                        ? 'bg-red-500/20 border-red-500 text-red-400'
                        : 'bg-zinc-900/50 border-white/10 text-zinc-400'
                      : isSelected
                      ? 'bg-primary text-black border-primary scale-105'
                      : 'bg-zinc-900/50 border-white/10 text-white hover:border-primary/50 hover:bg-zinc-800/50'
                  } ${showResult ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{option}</span>
                    {showColors && isCorrect && <CheckCircle className="w-5 h-5" />}
                    {showColors && isSelected && !isCorrect && <XCircle className="w-5 h-5" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizBattle;
